/**
 * Dropdown API Service
 * Provides dropdown options from Redux store (loaded on page mount)
 * No longer makes API calls - data is loaded once on page load via Redux
 */

import { LMADropdownResponse } from '../../../features/lma/dropdownSlice';

export interface DropdownOption {
  value: string;
  label: string;
}

/**
 * Transform dropdown options from Redux store data
 * @param responseKey - Which data to extract: 'cities', 'states', 'associatedProtocols', 'qualifiedProtocols', 'sites'
 * @param data - The dropdown data from Redux store
 * @param selectedProtocols - Optional array of selected protocol values to filter sites
 */
export const transformDropdownOptions = (
  responseKey:
    | 'cities'
    | 'states'
    | 'associatedProtocols'
    | 'qualifiedProtocols'
    | 'sites'
    | 'ethnicity'
    | 'userIds'
    | 'patientReferralStatuses',
  data: LMADropdownResponse | null,
  selectedProtocols?: string[]
): DropdownOption[] => {
  if (!data) {
    return [];
  }

  try {
    // Transform based on responseKey
    switch (responseKey) {
      case 'cities':
        return (data.cities || []).map((city) => ({
          value: city,
          label: city,
        }));

      case 'states':
        return (data.states || []).map((state) => ({
          value: state,
          label: state,
        }));

      case 'associatedProtocols':
        return (data.associatedProtocols || []).map((protocol) => ({
          value: protocol.protocol,
          label: `${protocol.protocol}`,
        }));

      case 'qualifiedProtocols':
        return (data.qualifiedProtocols || []).map((protocol) => ({
          value: protocol,
          label: protocol,
        }));

      case 'ethnicity':
        return (data.ethnicity || []).map((ethnicity) => ({
          value: ethnicity,
          label: ethnicity,
        }));
      case 'userIds': {
        const currentUserId = localStorage.getItem('lma-userId');
        const users = (data.lmaUsers || []).map((user) => ({
          value: String(user.userId),
          label: user.userName,
        }));

        return users.sort((a, b) => {
          if (a.value === currentUserId) return -1;
          if (b.value === currentUserId) return 1;
          return a.label.localeCompare(b.label);
        });
      }

      case 'patientReferralStatuses': {
        const statuses = Array.isArray(data.patientReferralStatuses)
          ? data.patientReferralStatuses
          : [];

        return statuses
          .map((status: any) => {
            if (typeof status === 'string') {
              return { value: status, label: status };
            }

            const value = status?.value || status?.label || status?.name;
            const label = status?.label || status?.name || status?.value;

            if (!value || !label) {
              return null;
            }

            return { value: String(value), label: String(label) };
          })
          .filter(Boolean) as DropdownOption[];
      }

      case 'sites': {
        // Extract unique sites from associatedProtocols
        // If protocols are selected, filter sites by those protocols
        // Otherwise, show all sites from all protocols
        const sites: DropdownOption[] = [];
        const seen = new Set<string>();

        const protocolsToUse = (data.associatedProtocols || []).filter((protocol) => {
          // If no protocols selected, show all sites
          if (!selectedProtocols || selectedProtocols.length === 0) {
            return true;
          }
          // If protocols selected, only show sites from those protocols
          return selectedProtocols.includes(protocol.protocol);
        });

        protocolsToUse.forEach((protocol) => {
          (protocol.sites || []).forEach((site) => {
            const sitename = site.name;
            if (sitename && !seen.has(sitename)) {
              seen.add(sitename);
              sites.push({
                value: sitename,
                label: site.name,
              });
            }
          });
        });

        // Sort sites by label
        return sites.sort((a, b) => a.label.localeCompare(b.label));
      }

      default:
        return [];
    }
  } catch (error) {
    return [];
  }
};
